import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import * as M from '@core/api/models';
import type {
  CreateInvestmentRequest,
  MediaOwnerAttachmentsReplaceRequest,
  MediaOwnerAttachmentsReplaceResponseDto,
} from '@core/api/contracts';
import { HTTP_ERROR_FEEDBACK_POLICY } from '@core/interceptors/http-error-feedback-policy.token';
import {
  MediaAttachmentsApiClient,
  MediaAttachmentsOwnerViewModel,
  MediaRoleCode,
} from '@core/media';
import { environment } from '../../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class InvestmentApiClient {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);
  private readonly mediaAttachmentsApi = inject(MediaAttachmentsApiClient);
  private readonly apiUrl: string;

  constructor() {
    const baseUrl = environment.apiUrl || 'http://localhost:8080';
    this.apiUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  }

  getInvestments(): Observable<M.InvestmentWithCountsDto[]> {
    return this.api.getInvestments();
  }

  getInvestment(investmentId: number): Observable<M.InvestmentDetailsWithCountsDto> {
    return this.api.getInvestment(investmentId);
  }

  getInvestmentSellingPoints(investmentId: number): Observable<M.PlaceDto[]> {
    return this.api.getInvestmentSellingPoints(investmentId);
  }

  createInvestment(data: CreateInvestmentRequest): Observable<number> {
    return this.http.post<number>(
      `${this.apiUrl}/api/v1/investments`,
      data,
      {
        context: new HttpContext().set(HTTP_ERROR_FEEDBACK_POLICY, 'feature-owned'),
        headers: jsonHeaders(),
      }
    );
  }

  updateInvestment(investmentId: number, data: M.InvestmentRequest): Observable<void> {
    return this.api.updateInvestment(investmentId, data);
  }

  updateInvestmentLocation(investmentId: number, data: M.InvestmentAddressRequest): Observable<void> {
    return this.api.updateInvestmentLocation(investmentId, data);
  }

  getInvestmentSelectOptions(): Observable<M.InvestmentSelectOptionDto[]> {
    return this.api.getInvestmentSelectOptions();
  }

  getDeveloper(): Observable<M.DeveloperDto> {
    return this.http.get<M.DeveloperDto>(`${this.apiUrl}/api/v1/developers`);
  }

  getCompanies(): Observable<M.CompanyDto[]> {
    return this.http.get<M.CompanyDto[]>(
      `${this.apiUrl}/api/v1/companies`
    );
  }

  getInvestmentAttachments(
    investmentId: number,
    roles?: MediaRoleCode[]
  ): Observable<MediaAttachmentsOwnerViewModel> {
    return this.mediaAttachmentsApi.getOwnerAttachments('INVESTMENT', investmentId, roles);
  }

  replaceInvestmentAttachments(
    request: MediaOwnerAttachmentsReplaceRequest
  ): Observable<MediaOwnerAttachmentsReplaceResponseDto> {
    return this.mediaAttachmentsApi.replaceOwnerAttachments(request);
  }
}

function jsonHeaders(): HttpHeaders {
  return new HttpHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });
}
